import React, { useState, useEffect } from 'react';
import { Layout, Row, Col } from 'antd';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import InputAdornment from '@material-ui/core/InputAdornment';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useDispatch, useSelector } from 'react-redux';
import ContentModelFields from './ContentModelFields';
import NewFieldModal from './NewFieldModal';
import InnerHeader from '../../component/InnerHeader';
import InfoMenu from '../../component/InfoMenu';
import { CInput, COutlinedInput } from '../../component/Input';
import CButton from '../../component/Button';
import { setCustomTypeDraft } from '../../actions/CustomTypeAction';
import Loader from '../../component/Loader/Loader';
import { createCustomType } from '../../services/napi';

const ContentModelNew = () => {
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = React.useState(false);
  const [isSaveEnabled, setSaveEnabled] = useState(true);

  const toggleModal = () => {
    setModalVisible(!modalVisible);
  };

  const onModalOk = ({ name, apiId, type }) => {
    dispatch(
      setCustomTypeDraft({
        ...customTypeDraft,
        template: {
          ...customTypeDraft.template,
          [apiId]: {
            field_name: apiId,
            field_type: type,
            label: name
          }
        }
      })
    );
    toggleModal();
  };

  const onSave = async () => {
    setSaveEnabled(false);
    await createCustomType(customTypeDraft, currentProject._id);
    setSaveEnabled(true);
    onBack();
  };

  const actions = [
    { onClick: () => window.history.back(), title: 'Cancel' },
    {
      disabled: !isSaveEnabled,
      onClick: onSave,
      title: 'Save',
      type: 'primary'
    }
  ];

  const sections = [
    {
      child: () => {
        return (
          <CButton block onClick={toggleModal} size="large" type="primary">
            Add Field
          </CButton>
        );
      },
      subtitle: 'The content type has used 0 out of 50 fields.',
      title: 'Fields'
    },
    {
      child: () => {},
      subtitle:
        'Change the entry editor’s appearance for this content type in the Entry editor settings',
      title: 'Entry editor appearance'
    },
    {
      child: () => {
        return (
          <COutlinedInput
            disabled
            endAdornment={
              <InputAdornment position="end">
                <FileCopyIcon />
              </InputAdornment>
            }
            id="outlined-flexible"
            onClick={() => navigator.clipboard.writeText(customTypeDraft.api_id)}
            value={customTypeDraft.api_id}
            variant="outlined"
            xstyle={{ width: '100%' }}
          />
        );
      },
      subtitle: 'Use this ID to retrieve everything related to this content type via the API.',
      title: 'Content type ID'
    },
    {
      child: () => {},
      subtitle: `Read more about content types in our guide to content modelling. \n\nTo learn more about the various ways of disabling and deleting fields have a look at the field lifecycle.`,
      title: 'Documentation'
    }
  ];

  const onBack = () => {
    window.history.back();
  };

  const setDraft = (customType) => {
    dispatch(setCustomTypeDraft(customType));
  };

  const currentProject = useSelector(({ project }) => project.currentProject);

  useEffect(() => {
    async function setDraft() {
      dispatch(
        setCustomTypeDraft({
          api_id: '',
          content: [],
          isPublic: true,
          languages: ['en', 'ka', 'ru'],
          name: '',
          project: currentProject._id,
          template: [],
          type: 'single'
        })
      );
    }

    setDraft();
  }, [currentProject._id, dispatch]);

  const customTypeDraft = useSelector(({ customType }) => customType.customTypeDraft);

  if (!Object.keys(customTypeDraft).length) {
    return <Loader />;
  }

  return (
    <Layout>
      <InnerHeader actions={actions} onBack={onBack} title={customTypeDraft.name} />
      <NewFieldModal onCancel={toggleModal} onOk={onModalOk} visible={modalVisible} />

      <Layout
        style={{
          height: 'calc(100vh - 235px)',
          overflowY: 'scroll',
          paddingLeft: '24px'
        }}
      >
        <Row>
          <Col
            span={18}
            style={{
              paddingRight: '24px'
            }}
          >
            <Row justify="space-between">
              <Col span={12} style={{ paddingRight: '8px' }}>
                <CInput
                  helperText="Human-readable name"
                  id="outlined-flexible"
                  onChange={(event) => setDraft({ ...customTypeDraft, name: event.target.value })}
                  value={customTypeDraft.name}
                  variant="outlined"
                  xstyle={{ width: '100%' }}
                />
              </Col>
              <Col span={12} style={{ paddingLeft: '8px' }}>
                <CInput
                  helperText="Should be unique. Please add a prefix with project name"
                  id="outlined-flexible-api-id"
                  onChange={(event) => setDraft({ ...customTypeDraft, api_id: event.target.value })}
                  value={customTypeDraft.api_id}
                  variant="outlined"
                  xstyle={{ width: '100%' }}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: '40px' }}>
              <Col span={24}>
                <TextareaAutosize
                  aria-label="empty textarea"
                  onChange={(event) => {
                    return setDraft({
                      ...customTypeDraft,
                      description: event.target.value
                    });
                  }}
                  placeholder="Description"
                  style={{
                    background: '#EFF0F7',
                    border: '1px solid #D6D8E7',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 'normal',
                    height: '96px',
                    lineHeight: '24px',
                    padding: '16px',
                    width: '100%'
                  }}
                  value={customTypeDraft.description || ''}
                />
              </Col>
            </Row>
            <ContentModelFields template={customTypeDraft.template} />
          </Col>
          <Col span={6}>
            <InfoMenu sections={sections} />
          </Col>
        </Row>
      </Layout>
    </Layout>
  );
};

export default ContentModelNew;
